/**
 * Module Registry Configuration
 *
 * This file defines ALL core modules and their default enabled/disabled state.
 * The ORDER of entries in the `coreModules` array determines the ORDER they
 * appear in the sidebar menu.
 *
 * Tenant repos override this configuration by calling `configureModules()` with
 * their own overrides before the Vue app mounts. Tenants can:
 *   - Enable or disable any core module
 *   - Reorder modules by providing a new ordered list
 *   - Add tenant-specific modules via `registerTenantModules()`
 *
 * Tenant repos provide a `modules.config.json` file that is loaded at runtime:
 *
 *   {
 *     "modules": { ... },
 *     "sidebar": [ ... ],
 *     "account": { "children": [...], "actions": { ... } }
 *   }
 *
 * The `sidebar` array determines what appears in the sidebar rail and in what
 * order. Each entry is either a standalone module (`type: 'module'`) or a
 * category grouping (`type: 'category'`) that shows a flyout with children.
 */

import type { Component } from 'vue';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Configuration for a single module entry in the sidebar. */
export interface ModuleConfig {
  /** Unique identifier for the module. Used as the key everywhere. */
  id: string;
  /** Maps to the permission system's module ID (e.g. 'production-scheduler'). */
  moduleId: string;
  /** Human-readable label shown in tooltips. */
  name: string;
  /** Path to an SVG icon asset (imported via Vite). */
  iconUrl: string;
  /** Whether the global header bar is shown when this module is active. Defaults to true. */
  showGlobalHeader?: boolean;
  /**
   * Factory that returns the async component for this module.
   * We use a factory so that the import is only evaluated when the registry
   * is consumed, not when the config file is loaded.
   */
  componentLoader: () => Component;
  /** URL slug for this module. Becomes /<slug> in the route table. Required. */
  slug: string;
  /** Optional tab declarations. First entry is the default tab. */
  tabs?: { slug: string; name: string }[];
}

/** A standalone module entry in the sidebar. */
export interface SidebarModuleItem {
  id: string;
  type: 'module';
}

/** A category entry grouping multiple modules behind a flyout. */
export interface SidebarCategoryItem {
  id: string;
  type: 'category';
  label: string;
  icon: string;
  devOnly?: boolean;
  children: string[];
}

/** Union of sidebar entry types. */
export type SidebarItem = SidebarModuleItem | SidebarCategoryItem;

/** Account menu configuration. */
export interface AccountConfig {
  children: string[];
  actions: Record<string, boolean>;
}

/**
 * The tenant configuration loaded from modules.config.json.
 */
export interface TenantConfig {
  modules: Record<string, boolean>;
  sidebar: SidebarItem[];
  account: AccountConfig;
  /**
   * Module ID to land on when the user navigates to "/". Falls back to the
   * first sidebar-ordered module the user has permission for if unset, the
   * named module is disabled, or the user lacks permission.
   */
  defaultModule?: string;
}

/** A resolved sidebar module with full ModuleConfig attached. */
export interface ResolvedSidebarModule {
  type: 'module';
  module: ModuleConfig;
}

/** A resolved sidebar category with full ModuleConfig for each child. */
export interface ResolvedSidebarCategory {
  type: 'category';
  id: string;
  label: string;
  icon: string;
  devOnly: boolean;
  children: ModuleConfig[];
}

/** Union of resolved sidebar items. */
export type ResolvedSidebarItem = ResolvedSidebarModule | ResolvedSidebarCategory;

/** Resolved account menu. */
export interface ResolvedAccountMenu {
  children: ModuleConfig[];
  actions: { id: string; label: string; enabled: boolean }[];
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let _coreModules: ModuleConfig[] = [];
let _tenantModules: ModuleConfig[] = [];
let _tenantConfig: TenantConfig | null = null;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Human-readable labels for account actions. */
const ACCOUNT_ACTIONS: Record<string, string> = {
  'switch-user': 'Switch User',
  'sign-out': 'Sign Out',
};

// ---------------------------------------------------------------------------
// Registration API (called at app startup)
// ---------------------------------------------------------------------------

/**
 * Register the core module definitions. Called once from the appStore
 * initializer so that icon imports stay co-located with the store.
 */
export function registerCoreModules(modules: ModuleConfig[]): void {
  _coreModules = modules;
}

/**
 * Register tenant-specific modules. These are always appended after
 * the (possibly filtered/reordered) core modules.
 *
 * Call this in the tenant repo's entry point before the Vue app mounts.
 */
export function registerTenantModules(modules: ModuleConfig[]): void {
  _tenantModules = modules;
}

/**
 * Apply tenant configuration. Called by loadTenantModuleConfig() after
 * fetching modules.config.json, before the Vue app mounts.
 */
export function configureTenant(config: TenantConfig): void {
  _tenantConfig = config;
}

// ---------------------------------------------------------------------------
// Resolution API (called by the appStore)
// ---------------------------------------------------------------------------

/**
 * Resolve the final ordered list of enabled modules, merging core defaults
 * with tenant overrides and tenant-specific modules.
 */
export function resolveModules(): ModuleConfig[] {
  const allCoreModules = [..._coreModules];

  if (!_tenantConfig) {
    // No tenant config — all core modules in default order + tenant modules
    return [...allCoreModules, ..._tenantModules];
  }

  const ordered: ModuleConfig[] = [];

  for (const [id, enabled] of Object.entries(_tenantConfig.modules)) {
    if (!enabled) continue;

    const mod = allCoreModules.find(m => m.id === id)
      || _tenantModules.find(m => m.id === id);

    if (mod) {
      ordered.push(mod);
    } else {
      console.warn(`[ModuleRegistry] Module "${id}" is enabled but not found in core or tenant modules.`);
    }
  }

  // Append any tenant modules not referenced in the config
  const configuredIds = new Set(Object.keys(_tenantConfig.modules));
  for (const tm of _tenantModules) {
    if (!configuredIds.has(tm.id)) {
      ordered.push(tm);
    }
  }

  return ordered;
}

/**
 * Look up a module by ID from both core and tenant modules.
 * Returns undefined if the module is not found or is disabled.
 */
function findEnabledModule(id: string): ModuleConfig | undefined {
  const enabledModules = resolveModules();
  return enabledModules.find(m => m.id === id);
}

/**
 * Resolve the sidebar configuration into fully-resolved items.
 *
 * Processes the `sidebar` array from TenantConfig:
 * - Filters out disabled modules (not in the enabled set)
 * - Filters out devOnly categories when not in dev mode
 * - Resolves module IDs to full ModuleConfig objects
 * - Skips categories that end up with no enabled children
 *
 * When no tenant config is present, returns all modules as standalone items.
 */
export function resolveSidebar(): ResolvedSidebarItem[] {
  const enabledModules = resolveModules();

  if (!_tenantConfig) {
    // No tenant config — all modules as standalone sidebar items
    return enabledModules.map(mod => ({
      type: 'module' as const,
      module: mod,
    }));
  }

  const enabledSet = new Set(enabledModules.map(m => m.id));
  const result: ResolvedSidebarItem[] = [];

  for (const item of _tenantConfig.sidebar) {
    if (item.type === 'module') {
      const mod = enabledModules.find(m => m.id === item.id);
      if (mod) {
        result.push({ type: 'module', module: mod });
      }
    } else if (item.type === 'category') {
      // Skip devOnly categories in production. VITE_SHOW_DEV_TOOLS=true (set
      // per-env in the tenant's deployment/frontend env file) keeps them
      // visible on built test sites — used by Foreseeson-test for the
      // B-Track prototype modules.
      if (item.devOnly && !import.meta.env.DEV && import.meta.env.VITE_SHOW_DEV_TOOLS !== 'true') {
        continue;
      }

      const resolvedChildren: ModuleConfig[] = [];
      for (const childId of item.children) {
        if (!enabledSet.has(childId)) continue;
        const mod = enabledModules.find(m => m.id === childId);
        if (mod) {
          resolvedChildren.push(mod);
        }
      }

      // Skip empty categories
      if (resolvedChildren.length === 0) continue;

      result.push({
        type: 'category',
        id: item.id,
        label: item.label,
        icon: item.icon,
        devOnly: item.devOnly ?? false,
        children: resolvedChildren,
      });
    }
  }

  return result;
}

/**
 * Resolve the account menu configuration.
 *
 * - Resolves `account.children` module IDs to full ModuleConfig objects
 * - Maps `account.actions` to `{ id, label, enabled }` objects using ACCOUNT_ACTIONS lookup
 *
 * When no tenant config is present, returns an empty account menu.
 */
export function resolveAccountMenu(): ResolvedAccountMenu {
  if (!_tenantConfig) {
    return {
      children: [],
      actions: Object.entries(ACCOUNT_ACTIONS).map(([id, label]) => ({
        id,
        label,
        enabled: true,
      })),
    };
  }

  const children: ModuleConfig[] = [];
  for (const childId of _tenantConfig.account.children) {
    const mod = findEnabledModule(childId);
    if (mod) {
      children.push(mod);
    }
  }

  const actions = Object.entries(_tenantConfig.account.actions).map(([id, enabled]) => ({
    id,
    label: ACCOUNT_ACTIONS[id] ?? id,
    enabled,
  }));

  return { children, actions };
}

/**
 * Check whether a tenant config has been loaded.
 */
export function isConfigured(): boolean {
  return _tenantConfig !== null;
}

/**
 * Tenant-configured module ID to land on when navigating to "/".
 * Returns undefined when no tenant config is loaded or no default is set —
 * the router falls back to the first permitted module in that case.
 */
export function getDefaultModuleId(): string | undefined {
  return _tenantConfig?.defaultModule;
}

/**
 * Reset all registry state. Useful for testing.
 */
export function resetRegistry(): void {
  _coreModules = [];
  _tenantModules = [];
  _tenantConfig = null;
}
